import { useEffect, useMemo, useState } from "react";
import { PlatformApiClient, PlatformApiError } from "../api";
import { ActionBar, FormLayout, Section, SectionHeader, StatusState } from "../adaptive/primitives";
import { OperationalDetail, OperationalList, OperationalWorkspace, StatusBadge } from "../adaptive/operational";
import { menuMoney } from "./interpret";
import type { MenuItem, MenuOverview } from "./types";

export function MenuCatalogView({ slug, api, onExpired }: { slug: string; api: PlatformApiClient; onExpired: () => void }) {
  const [data, setData] = useState<MenuOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedDirty, setSelectedDirty] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  function handleFailure(cause: unknown, message: string) {
    if (cause instanceof PlatformApiError && cause.status === 401) { onExpired(); return; }
    setNotice(message);
  }

  const load = () => api.menu<MenuOverview>(slug, "/overview").then((value) => { setData(value); setError(null); }).catch((cause) => {
    if (cause instanceof PlatformApiError && cause.status === 401) { onExpired(); return; }
    setError(cause instanceof PlatformApiError && cause.status === 403 ? "No tenés permisos para administrar esta carta." : "No pudimos cargar la carta.");
  });
  useEffect(() => { void load(); }, [slug]);

  const categories = useMemo(() => (data?.categories ?? []).map((category) => ({
    ...category,
    items: category.items.filter((item) => {
      if (search && !item.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (status === "available" && !item.isAvailable) return false;
      if (status === "unavailable" && item.isAvailable) return false;
      return true;
    }),
  })).filter((category) => category.items.length > 0), [data, search, status]);

  const activeItems = useMemo(() => (data?.categories ?? []).flatMap((category) => category.items), [data]);
  const visibleItemIds = useMemo(() => new Set(categories.flatMap((category) => category.items.map((item) => item.id))), [categories]);
  const selectedItem = selectedItemId ? activeItems.find((item) => item.id === selectedItemId) ?? null : null;

  useEffect(() => {
    if (selectedItemId && !selectedItem) {
      setSelectedItemId(null);
      setSelectedDirty(false);
      return;
    }
    if (selectedItemId && !visibleItemIds.has(selectedItemId) && !selectedDirty) {
      setSelectedItemId(null);
      setSelectedDirty(false);
    }
  }, [selectedItemId, selectedItem, selectedDirty, visibleItemIds]);

  async function mutate(path: string, body: unknown) {
    try {
      const result = await api.menu<{ message?: string; status?: string }>(slug, path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
      setNotice(result.message ?? "Cambio aplicado.");
      await load();
      return result;
    } catch (cause) {
      handleFailure(cause, "No pudimos aplicar ese cambio.");
      return null;
    }
  }

  function selectItem(item: MenuItem) {
    if (selectedItemId === item.id) return;
    if (selectedItemId && selectedDirty) {
      setNotice("Guardá o descartá los cambios del producto seleccionado antes de editar otro.");
      return;
    }
    setSelectedItemId(item.id);
    setSelectedDirty(false);
  }

  function closeDetail() {
    setSelectedItemId(null);
    setSelectedDirty(false);
  }

  if (error) return <StatusState className="menu-state" tone="danger" live="assertive" title="No pudimos cargar la carta" message={error} />;
  if (!data) return <StatusState className="menu-state" tone="loading" title="Cargando carta…" message="Estamos preparando categorías, productos y disponibilidad." />;

  const catalogList = categories.length === 0
    ? <StatusState className="menu-empty" title="Sin resultados" message="No hay productos que coincidan con estos filtros." />
    : <div className="menu-category-stack">
      {categories.map((category) => <Section className="menu-category" key={category.id}>
        <SectionHeader
          className="menu-section-heading"
          title={category.name}
          description={category.description}
          actions={<span className="menu-section-count">{category.items.length} {category.items.length === 1 ? "producto" : "productos"}</span>}
        />
        <OperationalList className="menu-catalog-list" ariaLabel={`Productos de ${category.name}`}>
          {category.items.map((item) => <MenuCatalogRow
            key={item.id}
            item={item}
            currency={data.currency}
            selected={selectedItemId === item.id}
            availabilityDisabled={selectedItemId === item.id && selectedDirty}
            onSelect={() => selectItem(item)}
            onAvailability={() => void mutate(`/items/${encodeURIComponent(item.id)}/availability`, { available: !item.isAvailable })}
          />)}
        </OperationalList>
      </Section>)}
    </div>;

  return <div className="menu-stack">
    {notice && <div className="menu-notice" role="status">{notice}</div>}

    <FormLayout className="menu-toolbar" columns={2}>
      <label className="cd-visually-labelled-field"><span>Buscar productos</span><input aria-label="Buscar productos" placeholder="Buscar en tu carta…" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
      <label className="cd-visually-labelled-field"><span>Disponibilidad</span><select aria-label="Filtrar disponibilidad" value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">Todos</option><option value="available">Disponibles</option><option value="unavailable">Agotados</option></select></label>
    </FormLayout>

    <OperationalWorkspace
      className="menu-catalog-workspace"
      list={catalogList}
      detail={selectedItem
        ? <OperationalDetail className="menu-catalog-detail" sticky ariaLabel={`Editar ${selectedItem.name}`}>
          <MenuItemDetailEditor
            key={selectedItem.id}
            item={selectedItem}
            currency={data.currency}
            onDirtyChange={setSelectedDirty}
            onClose={closeDetail}
            onSave={async (payload) => { await mutate(`/items/${encodeURIComponent(selectedItem.id)}`, payload); }}
            onArchive={async () => {
              const result = await mutate(`/items/${encodeURIComponent(selectedItem.id)}/archive`, { expectedRevision: selectedItem.revision });
              if (result && result.status !== "blocked") closeDetail();
            }}
          />
        </OperationalDetail>
        : undefined}
    />

    {data.archivedItems.length > 0 && <Section className="menu-archived-section">
      <SectionHeader
        className="menu-section-heading"
        title="Archivados"
        description="Productos fuera de la carta activa. Podés restaurarlos sin recrearlos."
        actions={<span className="menu-section-count">{data.archivedItems.length}</span>}
      />
      <OperationalList className="menu-archived-list" ariaLabel="Productos archivados">
        {data.archivedItems.map((item) => <div className="menu-archived-row" role="listitem" key={item.id}>
          <div className="menu-archived-identity"><strong>{item.name}</strong><span>{item.categoryName}</span></div>
          <StatusBadge tone="neutral">Archivado</StatusBadge>
          <button type="button" aria-label={`Restaurar ${item.name}`} onClick={() => void mutate(`/items/${encodeURIComponent(item.id)}/restore`, { expectedRevision: item.revision })}>Restaurar</button>
        </div>)}
      </OperationalList>
    </Section>}
  </div>;
}

function MenuCatalogRow({
  item,
  currency,
  selected,
  availabilityDisabled,
  onSelect,
  onAvailability,
}: {
  item: MenuItem;
  currency: string;
  selected: boolean;
  availabilityDisabled: boolean;
  onSelect: () => void;
  onAvailability: () => void;
}) {
  return <div className={`menu-catalog-row${selected ? " is-selected" : ""}`} role="listitem" data-selected={selected ? "true" : "false"}>
    <button
      className="menu-catalog-row-select"
      type="button"
      aria-pressed={selected}
      aria-label={`Editar ${item.name}`}
      onClick={onSelect}
    >
      <span className="menu-catalog-thumb" aria-hidden="true">
        {item.imageUrl ? <img src={item.imageUrl} alt="" /> : <span className="menu-catalog-thumb-placeholder" />}
      </span>
      <span className="menu-catalog-identity">
        <strong>{item.name}</strong>
        <span>{item.description || item.categoryName}</span>
      </span>
      <span className="menu-catalog-price">{item.priceFrom == null ? "Sin precio" : `Desde ${menuMoney(item.priceFrom, currency)}`}</span>
      <StatusBadge tone={item.isAvailable ? "success" : "warning"} dot>{item.isAvailable ? "Disponible" : "Agotado"}</StatusBadge>
    </button>
    <button
      className="menu-availability-action"
      type="button"
      disabled={availabilityDisabled}
      aria-label={`${item.isAvailable ? "Marcar" : "Reactivar"} ${item.name}${item.isAvailable ? " como agotado" : ""}`}
      title={availabilityDisabled ? "Guardá o descartá los cambios antes de cambiar la disponibilidad." : undefined}
      onClick={onAvailability}
    >
      {item.isAvailable ? "Marcar agotado" : "Reactivar"}
    </button>
  </div>;
}

function MenuItemDetailEditor({
  item,
  currency,
  onDirtyChange,
  onClose,
  onSave,
  onArchive,
}: {
  item: MenuItem;
  currency: string;
  onDirtyChange: (dirty: boolean) => void;
  onClose: () => void;
  onSave: (payload: unknown) => Promise<void>;
  onArchive: () => Promise<void>;
}) {
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description ?? "");
  const [prices, setPrices] = useState<Record<string, string>>(() => Object.fromEntries(item.variants.map((variant) => [variant.id, String(variant.priceMinor / 100)])));
  const [busy, setBusy] = useState(false);

  const dirty = name !== item.name
    || description !== (item.description ?? "")
    || item.variants.some((variant) => Math.round(Number((prices[variant.id] ?? "0").replace(",", ".")) * 100) !== variant.priceMinor);

  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await onSave({
        patch: { name, description },
        expectedRevision: item.revision,
        variantChanges: item.variants.map((variant) => ({ variantId: variant.id, expectedRevision: variant.revision, priceMinor: Math.round(Number((prices[variant.id] ?? "0").replace(",", ".")) * 100) })).filter((change, index) => change.priceMinor !== item.variants[index]!.priceMinor),
        newVariants: [],
      });
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    setBusy(true);
    try {
      await onArchive();
    } finally {
      setBusy(false);
    }
  }

  return <form className="menu-detail-form" onSubmit={submit}>
    <div className="menu-detail-heading">
      <div>
        <p className="menu-detail-eyebrow">Edición contextual</p>
        <h2>{item.name}</h2>
        <p>Editá los datos disponibles sin perder de vista la carta.</p>
      </div>
      <button type="button" onClick={onClose}>{dirty ? "Descartar cambios" : "Cerrar detalle"}</button>
    </div>

    <div className="menu-detail-summary">
      <span className="menu-detail-image" aria-hidden="true">
        {item.imageUrl ? <img src={item.imageUrl} alt="" /> : <span className="menu-detail-image-placeholder">Sin imagen</span>}
      </span>
      <div>
        <StatusBadge tone={item.isAvailable ? "success" : "warning"} dot>{item.isAvailable ? "Disponible" : "Agotado"}</StatusBadge>
        <p>{item.priceFrom == null ? "Sin precio publicado" : `Precio desde ${menuMoney(item.priceFrom, currency)}`}</p>
      </div>
    </div>

    <FormLayout className="menu-detail-fields" columns={1}>
      <label>Nombre<input value={name} onChange={(event) => setName(event.target.value)} required /></label>
      <label>Descripción<textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={3} /></label>
      {item.variants.map((variant) => <label key={variant.id}>{variant.name} ({currency})<input inputMode="decimal" value={prices[variant.id] ?? ""} onChange={(event) => setPrices((current) => ({ ...current, [variant.id]: event.target.value }))} /></label>)}
    </FormLayout>

    <div className="menu-detail-context" role="group" aria-label="Contexto de solo lectura">
      <MenuReadOnlyFact label="Categoría" values={[item.categoryName]} />
      <MenuReadOnlyFact label="Alérgenos" values={item.allergens} empty="Sin alérgenos informados" />
      <MenuReadOnlyFact label="Preferencias" values={item.dietaryClaims} empty="Sin preferencias informadas" />
      <MenuReadOnlyFact label="Modificadores" values={item.modifierGroups.map((group) => group.name)} empty="Sin grupos de modificadores" />
    </div>

    <ActionBar className="menu-detail-actions" sticky="never">
      <button className="primary-button" disabled={busy || !dirty} type="submit">{busy ? "Guardando…" : "Guardar cambios"}</button>
    </ActionBar>

    <div className="menu-detail-danger-zone">
      <div><strong>Archivar producto</strong><p>Lo saca de la carta activa, pero conserva la posibilidad de restaurarlo.</p></div>
      <button className="menu-danger-link" type="button" disabled={busy || dirty} title={dirty ? "Guardá o descartá los cambios antes de archivar." : undefined} onClick={() => void archive()}>Archivar</button>
    </div>
  </form>;
}

function MenuReadOnlyFact({ label, values, empty }: { label: string; values: string[]; empty?: string }) {
  return <div className="menu-readonly-fact">
    <span>{label}</span>
    <strong>{values.length > 0 ? values.join(" · ") : empty ?? "Sin datos"}</strong>
  </div>;
}
